/**
 * PortalPhotoHandoff — getting a job's photos out of Groundwork and into a
 * city portal's upload picker.
 *
 * THE PROBLEM THIS SOLVES (#1100, Corbin 2026-08-20). The portals increasingly
 * require photos to be *uploaded* with the test. Our capture flow deliberately
 * takes photos INTO the job so a tech never has to manage their camera roll —
 * which means at filing time the camera roll is empty and there is nothing to
 * upload. The photo has to be handed back to the phone before the portal can
 * take it.
 *
 * The mechanism was already written once, for the office's portal-entry wizard:
 * share the **device-stamped** copies through the share sheet so they land in
 * Photos, where every portal's upload picker looks first, with identity burned
 * into the pixels so a portal full of near-identical backflow photos is not a
 * guessing game. It lived only on the job page, under an "the office files
 * this" heading (#796) that correctly tells a tester nothing there is theirs.
 * The tester's own filing card had a thinner copy — a zip link and buttons
 * labelled with filenames chopped at 14 characters. This component is that
 * mechanism in one place, so the two surfaces cannot drift apart again.
 *
 * Two deliberate differences from the old filing-card block:
 *
 *  - **No zip on a phone.** A zip lands in Files as an archive and a portal's
 *    upload picker cannot read inside it, so "save everything" saves separate
 *    files by default. The office keeps `bulkSave="zip"`, where a desktop can
 *    actually unpack one.
 *  - **The empty case says so.** The old block was gated on `photos.length > 0`
 *    and rendered nothing at all, which is indistinguishable from an app that
 *    cannot do photos — and is why the whole capability was missing from the
 *    field guide nobody could tell was wrong.
 */
import { useState, type ReactNode } from "react";
import { toast } from "sonner";
import { Download, ExternalLink, ImageDown, Loader2, Share2 } from "lucide-react";

import { AttachmentImage } from "@/components/attachment-image";
import { Button } from "@/components/ui/button";
import { downloadAuthedFile, downloadUrlAsFile, fetchAuthedBlob, getToken } from "@/lib/api";
import { isStandalone } from "@/lib/standalone";
import { cn } from "@/lib/utils";

export interface HandoffPhoto {
    id: string;
    fileName: string | null;
    /** The capture slot ("Gauge", "Nameplate"), when the job records one. */
    slotLabel?: string | null;
}

/** Feature-detect Web Share with files (refused inside some installed PWAs). */
export function canShareFiles(): boolean {
    try {
        if (typeof navigator === "undefined" || typeof navigator.share !== "function" || !navigator.canShare) {
            return false;
        }
        const probe = new File([new Uint8Array(4)], "probe.jpg", { type: "image/jpeg" });
        return navigator.canShare({ files: [probe] });
    } catch {
        return false;
    }
}

/**
 * What a photo tile is called. The slot name is the useful one — the portal
 * asks for "the gauge photo", not for IMG_20260816_113402.jpg — so a filename
 * is never used as a caption; it is only ever the saved file's name.
 */
export function photoCaption(photo: HandoffPhoto, index: number): string {
    const slot = (photo.slotLabel ?? "").trim();
    return slot || `Photo ${index + 1}`;
}

/** The name a saved copy lands under. */
export function photoFileName(photo: HandoffPhoto, index: number): string {
    const name = (photo.fileName ?? "").trim();
    return name || `photo-${index + 1}.jpg`;
}

interface Props {
    photos: HandoffPhoto[];
    /**
     * What the stamp burns into the pixels — shown so the tech knows what the
     * city will be looking at. Typically device + account.
     */
    stampLabel?: string | null;
    /** How "save everything" behaves when the share sheet is unavailable. */
    bulkSave?: "files" | "zip";
    /** Replaces the default no-photos message. */
    empty?: ReactNode;
    /**
     * Supplied by surfaces that can mint a link back to themselves, for the
     * installed-PWA case where file sharing is refused outright.
     */
    onOpenInSafari?: () => void;
    className?: string;
}

export function PortalPhotoHandoff({
    photos,
    stampLabel,
    bulkSave = "files",
    empty,
    onOpenInSafari,
    className,
}: Props) {
    const [busy, setBusy] = useState<"share" | "all" | null>(null);
    const shareSupported = canShareFiles();

    if (photos.length === 0) {
        return (
            <div
                data-testid="portal-photos-empty"
                className={cn(
                    "rounded-lg border border-dashed border-border px-3 py-3 text-center text-xs leading-relaxed text-muted-foreground",
                    className,
                )}
            >
                {empty ?? (
                    <>
                        No photos on this job.
                        <br />
                        If the city asks for one, open the job and add it — it&rsquo;ll show up here.
                    </>
                )}
            </div>
        );
    }

    const saveOne = async (photo: HandoffPhoto, index: number) => {
        try {
            await downloadAuthedFile(
                `/api/attachments/${photo.id}/stamped?disposition=attachment`,
                photoFileName(photo, index),
            );
        } catch {
            toast.error("Could not save that photo — try again in a sec.");
        }
    };

    const share = async () => {
        setBusy("share");
        try {
            const files: File[] = [];
            for (const [i, p] of photos.entries()) {
                const blob = await fetchAuthedBlob(`/api/attachments/${p.id}/stamped`);
                files.push(new File([blob], photoFileName(p, i), { type: "image/jpeg" }));
            }
            if (navigator.canShare?.({ files })) {
                await navigator.share({ files, title: "Portal photos" });
                toast.success("Saved — pick them from Recents in the portal's upload");
                return;
            }
            // Batch refused (some Safari versions cap file counts) — one at a
            // time before giving up, so a three-photo job still gets through.
            for (const f of files) {
                if (!navigator.canShare?.({ files: [f] })) throw new Error("share-unavailable");
                await navigator.share({ files: [f], title: f.name });
            }
            toast.success("Saved — pick them from Recents in the portal's upload");
        } catch (err) {
            if ((err as Error)?.name === "AbortError") return; // the tech closed the sheet
            toast.error("Could not share photos — use Save below instead");
        } finally {
            setBusy(null);
        }
    };

    const saveAll = async () => {
        setBusy("all");
        try {
            if (bulkSave === "zip") {
                const params = new URLSearchParams({ ids: photos.map((p) => p.id).join(","), stamped: "1" });
                const token = getToken();
                if (token) params.set("token", token);
                await downloadUrlAsFile(`/api/attachments/zip?${params.toString()}`, "portal-photos.zip");
                return;
            }
            // Separately, on purpose — see the header note about zips.
            for (const [i, p] of photos.entries()) {
                await downloadAuthedFile(
                    `/api/attachments/${p.id}/stamped?disposition=attachment`,
                    photoFileName(p, i),
                );
            }
        } catch {
            toast.error("Could not save the photos — try one at a time.");
        } finally {
            setBusy(null);
        }
    };

    return (
        <div className={cn("flex flex-col gap-2.5", className)} data-testid="portal-photo-handoff">
            <div className="grid grid-cols-3 gap-2">
                {photos.map((p, i) => (
                    <button
                        key={p.id}
                        type="button"
                        onClick={() => void saveOne(p, i)}
                        title={`Save ${photoCaption(p, i)}`}
                        className="overflow-hidden rounded-lg border border-border text-left hover:border-primary focus-visible:border-primary"
                    >
                        <AttachmentImage
                            attachmentId={p.id}
                            alt={photoCaption(p, i)}
                            variant="thumb"
                            loading="lazy"
                            className="aspect-[4/3] w-full bg-muted object-cover"
                        />
                        <span className="block truncate border-t border-border px-1.5 py-1 text-center text-[10px] font-semibold text-muted-foreground">
                            {photoCaption(p, i)}
                        </span>
                    </button>
                ))}
            </div>

            {shareSupported ? (
                <Button type="button" size="lg" className="h-12 w-full" disabled={busy !== null} onClick={share}>
                    {busy === "share" ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                        <Share2 className="mr-2 h-4 w-4" />
                    )}
                    Send {photos.length} photo{photos.length === 1 ? "" : "s"} to Photos
                </Button>
            ) : (
                <>
                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed dark:border-amber-900 dark:bg-amber-950/50">
                        {isStandalone()
                            ? "This phone won't hand photos to another app from inside the installed Groundwork app."
                            : "This browser can't hand photos straight to another app."}{" "}
                        Save them instead:
                    </div>
                    {isStandalone() && onOpenInSafari && (
                        <Button
                            type="button"
                            variant="outline"
                            size="lg"
                            className="h-12 w-full"
                            onClick={onOpenInSafari}
                        >
                            <ExternalLink className="mr-2 h-4 w-4" />
                            Open in Safari to send photos
                        </Button>
                    )}
                    <Button
                        type="button"
                        variant="outline"
                        size="lg"
                        className="h-12 w-full"
                        disabled={busy !== null}
                        onClick={() => void saveAll()}
                    >
                        {busy === "all" ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : bulkSave === "zip" ? (
                            <Download className="mr-2 h-4 w-4" />
                        ) : (
                            <ImageDown className="mr-2 h-4 w-4" />
                        )}
                        {bulkSave === "zip" ? "Save all (zip)" : `Save all ${photos.length} to Files`}
                    </Button>
                </>
            )}

            <p className="text-[11px] leading-snug text-muted-foreground">
                {stampLabel
                    ? `Stamped with ${stampLabel} so the city can tell which device is which.`
                    : "Stamped with the device details so the city can tell which device is which."}{" "}
                Tap any photo to save just that one; delete them from Photos once the upload is done.
            </p>
        </div>
    );
}
